// In dev: empty string (Vite proxy handles /api/... routes)
// In staging: https://stagingapi.puresoft.ca (set via VITE_API_BASE_URL at build time)
export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''

// ── 401 handler — auto-clears stale auth on unauthorized response ────
// When the API returns 401, the stored token is invalid/expired.
// Clear auth state so the user is redirected to login.
// Public pages (roadmap, confirm) must not redirect unauthenticated visitors.
const PUBLIC_PATHS = ['/roadmap', '/login', '/setup', '/never-out/silence']
function isPublicPath(pathname: string) {
    return PUBLIC_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'))
}

function handle401() {
    try {
        const raw = localStorage.getItem('puresoft-auth')
        if (raw) {
            const parsed = JSON.parse(raw)
            if (parsed?.state) {
                parsed.state.user = null
                parsed.state.accessToken = null
                parsed.state.refreshToken = null
                parsed.state.isAuthenticated = false
                // Also drop impersonation state. An impersonation session can't be refreshed,
                // so it 401s here; if we left these flags set, the next sign-in would inherit a
                // stale "isImpersonating" and show the user impersonating themselves.
                parsed.state.originalUser = null
                parsed.state.originalAccessToken = null
                parsed.state.originalRefreshToken = null
                parsed.state.isImpersonating = false
                localStorage.setItem('puresoft-auth', JSON.stringify(parsed))
            }
        }
    } catch { /* swallow */ }
    // Only redirect to login from protected pages
    if (!isPublicPath(window.location.pathname)) {
        window.location.href = '/login'
    }
}

// ── Intercept raw fetch calls globally to catch 401s ──
const originalFetch = window.fetch
window.fetch = async (...args) => {
    const res = await originalFetch(...args)
    if (res.status === 401) {
        handle401()
    }
    return res
}

async function handleResponse(r: Response) {
    if (r.status === 401) {
        handle401()
        throw new Error('Session expired. Please sign in again.')
    }
    if (!r.ok) throw new Error(await r.text())
    return r.json()
}

export const api = (url: string, token: string | null) =>
    fetch(API_BASE + url, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } })
        .then(handleResponse)

/** Uploads a single file as multipart/form-data (no Content-Type header — the browser sets the boundary). */
export const apiUpload = (url: string, token: string | null, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return fetch(API_BASE + url, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: fd,
    }).then(handleResponse)
}

/** Resolves a server-relative image path (e.g. /uploads/...) to an absolute URL on the API origin. */
export const imageUrl = (path: string) =>
    path.startsWith('http') ? path : API_BASE + path

export const apiPost = (url: string, token: string | null, body: any = null) =>
    fetch(API_BASE + url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
    }).then(handleResponse)

export const apiPut = (url: string, token: string | null, body: any = null) =>
    fetch(API_BASE + url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
    }).then(handleResponse)

export const apiDelete = (url: string, token: string | null) =>
    fetch(API_BASE + url, {
        method: 'DELETE',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    }).then(handleResponse)
